import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Select from '@radix-ui/react-select';
import clsx from 'clsx';
import {
  Eye,
  MousePointerClick,
  Target,
  Ticket,
  Info,
  ChevronDown,
  Check,
  Shield,
} from 'lucide-react';
import { PlatformSelector } from '@/shared/components/promote/PlatformSelector/PlatformSelector';
import { AppInput } from '@/shared/components/ui/AppInput/AppInput';
import { AppSelect } from '@/shared/components/ui/AppSelect/AppSelect';
import { AppButton } from '@/shared/components/ui/AppButton/AppButton';
import { AppCard } from '@/shared/components/ui/AppCard/AppCard';
import { AppBadge } from '@/shared/components/ui/AppBadge/AppBadge';
import { BudgetInput } from '@/shared/components/promote/BudgetInput/BudgetInput';
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner/LoadingSpinner';
import { FEATURE_FLAGS } from '@/shared/config/feature-flags';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag';
import type { AdPlatform, CampaignObjective } from '@/shared/types/enums';
import { useUIStore } from '@/shared/stores/ui-store';
import { todayInputValue } from '@/shared/utils/date-input';
import { BudgetPacingBreakdown } from './BudgetPacingBreakdown/BudgetPacingBreakdown';
import { repairPersistedSchedule, resolveLaunchableSchedule } from './ObjectiveBudgetStep.logic';
import {
  objectiveBudgetSchema,
  type ObjectiveBudgetFormValues,
  formatMinBudget,
  getMinDailyBudget,
  requiresPixel,
} from '../../../schemas/objective-budget.schema';
import { ConversionPixelFields } from './ConversionPixelFields';
import { OBJECTIVE_OPTIONS } from '../../../types/campaigns.types';
import { useAccountsByPlatform, useEvents } from '../../../api/campaigns-queries';
import { useBudgetSuggestion, useSetDefaultPage } from '../../../api/campaigns-mutations';
import { MetaPagePicker } from './MetaPagePicker/MetaPagePicker';
import { EventPicker } from './EventPicker/EventPicker';
import { hasEventEnded } from './EventPicker/EventPicker.logic';
import { useCampaignWizardStore } from '../../../stores/campaign-wizard-store';
import { PLATFORM_OBJECTIVES, PLATFORM_OBJECTIVE_NOTES } from '../../../logic/platform-constraints';
import { useWizardInvalidToast } from '../../../hooks/use-wizard-invalid-toast/use-wizard-invalid-toast';
import styles from './ObjectiveBudgetStep.module.scss';

const OBJECTIVE_ICONS: Record<string, typeof Eye> = {
  eye: Eye,
  'mouse-pointer-click': MousePointerClick,
  target: Target,
  ticket: Ticket,
};

interface ObjectiveBudgetStepProps {
  onNext: () => void;
  onBack: () => void;
  onSaveDraft: () => void;
  isSaving: boolean;
  showBack?: boolean;
}

/**
 * Default the budget strategy to `lifetime` when we already know the
 * campaign duration (user picked start+end in a prior visit). Without a
 * schedule, daily is a safer starting point.
 */
function pickDefaultStrategy(
  existing: 'daily' | 'lifetime' | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
): 'daily' | 'lifetime' {
  if (existing) return existing;
  return startDate && endDate ? 'lifetime' : 'daily';
}

/**
 * Wizard Step 2: Set campaign objective and budget.
 */
export function ObjectiveBudgetStep({
  onNext,
  onBack,
  onSaveDraft,
  isSaving,
  showBack = true,
}: ObjectiveBudgetStepProps) {
  const { formData, updateFormData, markStepComplete } = useCampaignWizardStore();
  const addToast = useUIStore((s) => s.addToast);
  const budgetSuggestionMutation = useBudgetSuggestion();
  const budgetRecommendationEnabled = useFeatureFlag(FEATURE_FLAGS.AI_BUDGET_RECOMMENDATION);
  const eventsManagedEnabled = useFeatureFlag(FEATURE_FLAGS.EVENTS_MANAGED_CAMPAIGNS);
  const { data: events, isLoading: eventsLoading, isError: eventsError } = useEvents();

  const form = useForm<ObjectiveBudgetFormValues>({
    resolver: zodResolver(objectiveBudgetSchema),
    defaultValues: {
      managementType: formData.managementType ?? 'SELF_MANAGED',
      platform: formData.platform,
      adAccountId: formData.adAccountId ?? '',
      pageId: formData.pageId ?? '',
      pageName: formData.pageName ?? '',
      name: formData.name ?? '',
      objective: formData.objective,
      budgetStrategy: pickDefaultStrategy(
        formData.budgetStrategy,
        formData.startDate,
        formData.endDate,
      ),
      dailyBudgetCents: formData.dailyBudgetCents ?? 0,
      totalBudgetCents: formData.totalBudgetCents ?? 0,
      currency: formData.currency ?? 'USD',
      startDate: formData.startDate ?? '',
      endDate: formData.endDate ?? '',
      pixelId: formData.pixelId ?? '',
      conversionEvent: formData.conversionEvent ?? 'PURCHASE',
      landingUrl: formData.landingUrl ?? '',
    },
  });
  const {
    control,
    handleSubmit,
    watch,
    register,
    setValue,
    formState: { errors },
  } = form;

  const onInvalidSubmit = useWizardInvalidToast();

  const managementType = watch('managementType');
  const isSelfManaged = managementType === 'SELF_MANAGED';
  const selectedPlatform = watch('platform');
  const selectedObjective = watch('objective');
  const budgetStrategy = watch('budgetStrategy');
  const dailyBudgetCents = watch('dailyBudgetCents');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const platform = selectedPlatform as AdPlatform;
  const minDailyBudget = getMinDailyBudget(platform);
  const { data: accounts, isLoading: accountsLoading } = useAccountsByPlatform(
    selectedPlatform ?? '',
  );

  // The wizard store is sessionStorage-backed, so a draft started days ago
  // comes back with a start date that has since elapsed. Pull it forward on
  // mount — both in the form and in the store, since "Save as Draft" posts the
  // stored values without going through validation.
  useEffect(() => {
    const repaired = repairPersistedSchedule(formData, todayInputValue());
    if (!repaired) return;
    setValue('startDate', repaired.startDate, { shouldValidate: true });
    setValue('endDate', repaired.endDate, { shouldValidate: true });
    updateFormData(repaired);
    // Mount only: re-running would fight the organizer as they edit the dates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A draft saved while the flag was on can come back with EVENTS_MANAGED
  // selected after the flag is turned off — fall back to self-managed rather
  // than leaving the form pointed at a now-unselectable option.
  useEffect(() => {
    if (!eventsManagedEnabled && managementType === 'EVENTS_MANAGED') {
      setValue('managementType', 'SELF_MANAGED', { shouldDirty: true });
    }
  }, [eventsManagedEnabled, managementType, setValue]);

  // A draft saved while the flag was on can come back with EVENTS_MANAGED
  // selected after the flag is turned off — fall back to self-managed rather
  // than leaving the form pointed at a now-unselectable option.
  useEffect(() => {
    if (!eventsManagedEnabled && managementType === 'EVENTS_MANAGED') {
      setValue('managementType', 'SELF_MANAGED', { shouldDirty: true });
    }
  }, [eventsManagedEnabled, managementType, setValue]);

  // Surface event-fetch errors once. Not blocking, but the picker will show
  // no events until the organizer reloads.
  useEffect(() => {
    if (eventsError) {
      addToast({
        message: "Couldn't load your organization's events. Please try again.",
        variant: 'warning',
      });
    }
  }, [eventsError, addToast]);

  // A persisted eventId can outlive the event itself — the EventPicker
  // dropdown excludes ended events, but a sessionStorage draft started before
  // the event ended still points at it. Clear the stale selection once the
  // events list confirms it's ended, rather than letting the trigger keep
  // showing (and downstream logic keep treating) an event that's no longer a
  // valid choice.
  useEffect(() => {
    if (!events || !formData.eventId) return;
    const selectedEvent = events.find((evt) => evt.id === formData.eventId);
    if (selectedEvent && hasEventEnded(selectedEvent, todayInputValue())) {
      updateFormData({ eventId: undefined, eventName: undefined, eventVenue: undefined });
      addToast({
        message: 'The event you selected has ended. Please choose an upcoming event.',
        variant: 'warning',
      });
    }
  }, [events, formData.eventId, updateFormData, addToast]);

  // Filter objectives based on selected platform
  const filteredObjectiveOptions = useMemo(() => {
    if (!selectedPlatform) return OBJECTIVE_OPTIONS;

    const availableObjectives = PLATFORM_OBJECTIVES[selectedPlatform as AdPlatform];
    if (!availableObjectives) return OBJECTIVE_OPTIONS;

    return OBJECTIVE_OPTIONS.filter((opt) => availableObjectives.includes(opt.value));
  }, [selectedPlatform]);

  // Get platform-specific note for the selected objective
  const objectiveNote = useMemo(() => {
    if (!selectedPlatform || !selectedObjective) return null;
    const platformNotes = PLATFORM_OBJECTIVE_NOTES[selectedPlatform as AdPlatform];
    return platformNotes?.[selectedObjective as CampaignObjective] ?? null;
  }, [selectedPlatform, selectedObjective]);

  useEffect(() => {
    if (selectedPlatform && selectedPlatform !== formData.platform) {
      setValue('adAccountId', '');
      // Picking a different platform invalidates the previously chosen
      // Meta Page (pages belong to a Meta ad account). Wipe so the
      // picker re-renders fresh when the user comes back to META.
      setValue('pageId', '');
      setValue('pageName', '');
    }
  }, [selectedPlatform, formData.platform, setValue]);

  const watchedAdAccountId = watch('adAccountId');
  // Same reasoning at the ad-account level — switching accounts within
  // META should drop the previous Page selection so the user re-picks
  // from the new account's Pages.
  useEffect(() => {
    if (watchedAdAccountId !== formData.adAccountId) {
      setValue('pageId', '');
      setValue('pageName', '');
    }
  }, [watchedAdAccountId, formData.adAccountId, setValue]);

  // Clear objective if it becomes unavailable for the new platform
  useEffect(() => {
    if (!selectedPlatform || !selectedObjective) return;

    const availableObjectives = PLATFORM_OBJECTIVES[selectedPlatform as AdPlatform];
    if (
      availableObjectives &&
      !availableObjectives.includes(selectedObjective as CampaignObjective)
    ) {
      setValue('objective', '' as CampaignObjective);
      addToast({
        message: `The selected objective is not available for ${selectedPlatform}. Please choose a new one.`,
        variant: 'warning',
      });
    }
    // Only run when platform changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform]);

  // Fetch AI budget suggestion when objective is selected. Skipped entirely
  // while the flag is off so we never pay for a suggestion we won't show.
  useEffect(() => {
    if (!budgetRecommendationEnabled) return;
    if (selectedObjective && platform) {
      const durationDays =
        startDate && endDate
          ? Math.max(
              1,
              Math.floor(
                (new Date(endDate).getTime() - new Date(startDate).getTime()) /
                  (1000 * 60 * 60 * 24),
              ) + 1,
            )
          : undefined;

      // Anchor the suggestion on the real event date so the backend can
      // compute daysUntilEvent (pacing) and a suggested schedule instead of
      // guessing a duration when the user hasn't picked dates yet.
      const selectedEvent = events?.find((evt) => evt.id === formData.eventId);

      budgetSuggestionMutation.mutate({
        objective: selectedObjective,
        platform,
        durationDays,
        eventDate: selectedEvent?.startDate ?? undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    budgetRecommendationEnabled,
    selectedObjective,
    platform,
    startDate,
    endDate,
    formData.eventId,
    events,
  ]);

  // Auto-compute the lifetime figure from daily*days ONLY when the user is
  // in daily mode. In lifetime mode the user enters the number directly so
  // we must not overwrite it here.
  useEffect(() => {
    if (budgetStrategy !== 'daily') return;
    if (!dailyBudgetCents || !startDate || !endDate) {
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      return;
    }

    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const inclusiveDays = Math.floor((end.getTime() - start.getTime()) / millisecondsPerDay) + 1;

    setValue('totalBudgetCents', dailyBudgetCents * inclusiveDays, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [budgetStrategy, dailyBudgetCents, endDate, setValue, startDate]);

  const setDefaultPageMutation = useSetDefaultPage();

  const onSubmit = async (data: ObjectiveBudgetFormValues) => {
    // Zero out the field that doesn't match the chosen strategy so the
    // backend payload carries exactly one non-zero budget (Meta rejects
    // launches that send both daily AND lifetime).
    const normalized: ObjectiveBudgetFormValues =
      data.budgetStrategy === 'daily'
        ? { ...data, totalBudgetCents: 0 }
        : { ...data, dailyBudgetCents: 0 };

    // Persist the chosen Facebook Page on the ad account before moving
    // on. The backend launch flow reads `adAccount.defaultPageId` to fill
    // `object_story_spec.page_id`; without this PUT the rest of the
    // wizard would still pass validation locally but Meta would reject
    // the Ad on launch.
    if (normalized.adAccountId && normalized.pageId) {
      try {
        await setDefaultPageMutation.mutateAsync({
          adAccountId: normalized.adAccountId,
          pageId: normalized.pageId,
        });
      } catch (err) {
        addToast({
          message: `Couldn't save the Facebook Page selection: ${
            err instanceof Error ? err.message : 'Unknown error'
          }`,
          variant: 'error',
        });
        return;
      }
    }

    updateFormData(normalized);
    markStepComplete('objective-budget');
    onNext();
  };

  const handleApplyBudgetSuggestion = () => {
    const suggestion = budgetSuggestionMutation.data;
    if (!suggestion) return;

    const suggestedDaily = suggestion.payload['dailyBudgetCents'] as number | undefined;
    const suggestedTotal = suggestion.payload['totalBudgetCents'] as number | undefined;
    const suggestedSchedule = suggestion.payload['suggestedSchedule'] as
      | { startDate: string; endDate: string }
      | undefined;

    // Fill the schedule only when the user hasn't picked dates yet. With
    // both dates set the suggestion was computed FROM them, so overwriting
    // would discard a choice the user already made.
    if (suggestedSchedule && (!startDate || !endDate)) {
      const schedule = resolveLaunchableSchedule(suggestedSchedule, todayInputValue());
      setValue('startDate', schedule.startDate, { shouldDirty: true, shouldValidate: true });
      setValue('endDate', schedule.endDate, { shouldDirty: true, shouldValidate: true });
    }

    // Apply only the value matching the chosen strategy; keep the other at 0
    // so we never ship both to the backend.
    if (budgetStrategy === 'daily') {
      if (suggestedDaily) {
        setValue('dailyBudgetCents', suggestedDaily, { shouldDirty: true, shouldValidate: true });
      }
      setValue('totalBudgetCents', 0, { shouldDirty: true });
    } else {
      if (suggestedTotal) {
        setValue('totalBudgetCents', suggestedTotal, { shouldDirty: true, shouldValidate: true });
      }
      setValue('dailyBudgetCents', 0, { shouldDirty: true });
    }
  };

  const handleStrategyChange = (next: 'daily' | 'lifetime') => {
    setValue('budgetStrategy', next, { shouldDirty: true, shouldValidate: true });
    // Drop stale data from the now-hidden input so it can't leak into the
    // launch payload.
    if (next === 'daily') {
      setValue('totalBudgetCents', 0, { shouldDirty: true, shouldValidate: true });
    } else {
      setValue('dailyBudgetCents', 0, { shouldDirty: true, shouldValidate: true });
    }
  };

  const activeAccounts = (accounts ?? []).filter((acc) => acc.status === 'active');
  const ownAccounts = activeAccounts.filter((acc) => !acc.isManaged);
  const managedAccounts = activeAccounts.filter((acc) => acc.isManaged);
  const hasGroupedAccounts = ownAccounts.length > 0 && managedAccounts.length > 0;
  const onlyManagedAccounts = ownAccounts.length === 0 && managedAccounts.length > 0;

  const accountOptions = activeAccounts.map((acc) => ({
    value: acc.id,
    label: acc.accountName,
  }));

  // Auto-select first managed account if only managed accounts exist
  useEffect(() => {
    const firstManaged = managedAccounts[0];
    if (onlyManagedAccounts && firstManaged && !watch('adAccountId')) {
      setValue('adAccountId', firstManaged.id, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyManagedAccounts, managedAccounts.length]);

  return (
    <form
      className={styles.step}
      onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}
      onKeyDown={(e) => {
        // Only the Continue button should submit the form; Enter in any
        // field must not advance the wizard on its own.
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
          e.preventDefault();
        }
      }}
      noValidate
    >
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Event</h2>
        <p className={styles.sectionDescription}>Select an event from your organization.</p>
        <AppCard className={styles.fieldCard}>
          <EventPicker
            events={events}
            isLoading={eventsLoading}
            selectedEventId={formData.eventId}
            eventName={formData.eventName ?? ''}
            onSelectEvent={(evt) => {
              updateFormData({
                eventId: evt.id,
                eventName: evt.name,
                eventVenue: evt.venue ?? undefined,
              });
              // Auto-fill wizard dates from the Events.com catalog entry so
              // the organizer doesn't retype them. They can still tweak the
              // inputs afterwards — we only pre-populate, not lock. Dates are
              // clamped to today because the catalog also returns events that
              // already happened, and no platform accepts a past start.
              const schedule = resolveLaunchableSchedule(evt, todayInputValue());
              setValue('startDate', schedule.startDate, {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('endDate', schedule.endDate, { shouldDirty: true, shouldValidate: true });
            }}
          />
        </AppCard>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Campaign Management</h2>
        <p className={styles.sectionDescription}>Choose how you want to run this campaign.</p>
        <Controller
          name="managementType"
          control={control}
          render={({ field }) => (
            <div className={styles.managementTypeGrid}>
              <button
                type="button"
                className={clsx(
                  styles.managementTypeCard,
                  field.value === 'SELF_MANAGED' && styles.managementTypeCardActive,
                )}
                onClick={() => field.onChange('SELF_MANAGED')}
              >
                <div className={styles.managementTypeIcon}>
                  <Target size={24} />
                </div>
                <div className={styles.managementTypeContent}>
                  <strong>Self-managed</strong>
                  <span>Use your own ad account and launch campaigns directly.</span>
                </div>
              </button>
              <button
                type="button"
                disabled={!eventsManagedEnabled}
                aria-disabled={!eventsManagedEnabled}
                className={clsx(
                  styles.managementTypeCard,
                  field.value === 'EVENTS_MANAGED' && styles.managementTypeCardActive,
                  !eventsManagedEnabled && styles.managementTypeCardDisabled,
                )}
                onClick={() => {
                  if (!eventsManagedEnabled) return;
                  field.onChange('EVENTS_MANAGED');
                  setValue('adAccountId', '');
                }}
              >
                <div className={styles.managementTypeIcon}>
                  <Shield size={24} />
                </div>
                <div className={styles.managementTypeContent}>
                  <strong>Managed by events.com</strong>
                  <span>Our team sets up and manages your ads. Campaigns go through review.</span>
                </div>
                {!eventsManagedEnabled && (
                  <AppBadge variant="neutral" size="sm" className={styles.managementTypeBadge}>
                    Coming soon
                  </AppBadge>
                )}
              </button>
            </div>
          )}
        />
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Advertising Platform</h2>
        <Controller
          name="platform"
          control={control}
          render={({ field }) => (
            <div className={styles.platformWrapper}>
              <PlatformSelector value={field.value} onChange={field.onChange} />
              {errors.platform && (
                <span className={styles.error} role="alert">
                  {errors.platform.message}
                </span>
              )}
            </div>
          )}
        />
      </div>

      {selectedPlatform && isSelfManaged && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Ad Account</h2>
          <AppCard className={styles.fieldCard}>
            {accountsLoading ? (
              <LoadingSpinner />
            ) : accountOptions.length === 0 ? (
              <div className={styles.noAccounts}>
                <p>No connected accounts found for this platform.</p>
              </div>
            ) : hasGroupedAccounts ? (
              <Controller
                name="adAccountId"
                control={control}
                render={({ field }) => (
                  <div className={styles.selectWrapper}>
                    <label className={styles.selectLabel}>Ad Account</label>
                    <Select.Root value={field.value} onValueChange={field.onChange}>
                      <Select.Trigger
                        className={clsx(
                          styles.selectTrigger,
                          errors.adAccountId && styles.selectTriggerError,
                        )}
                        aria-invalid={!!errors.adAccountId}
                      >
                        <Select.Value placeholder="Select an ad account..." />
                        <Select.Icon className={styles.selectIcon}>
                          <ChevronDown size={16} />
                        </Select.Icon>
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Content
                          className={styles.selectContent}
                          position="popper"
                          sideOffset={4}
                        >
                          <Select.Viewport className={styles.selectViewport}>
                            <Select.Group>
                              <Select.Label className={styles.selectGroupLabel}>
                                Your Accounts
                              </Select.Label>
                              {ownAccounts.map((acc) => (
                                <Select.Item
                                  key={acc.id}
                                  value={acc.id}
                                  className={styles.selectItem}
                                >
                                  <Select.ItemText>{acc.accountName}</Select.ItemText>
                                  <Select.ItemIndicator className={styles.selectItemIndicator}>
                                    <Check size={14} />
                                  </Select.ItemIndicator>
                                </Select.Item>
                              ))}
                            </Select.Group>
                            <Select.Separator className={styles.selectSeparator} />
                            <Select.Group>
                              <Select.Label className={styles.selectGroupLabel}>
                                <Shield size={12} />
                                Managed by events.com
                              </Select.Label>
                              {managedAccounts.map((acc) => (
                                <Select.Item
                                  key={acc.id}
                                  value={acc.id}
                                  className={styles.selectItem}
                                >
                                  <Select.ItemText>
                                    <span className={styles.managedItemLabel}>
                                      {acc.accountName}
                                      <AppBadge variant="info" size="sm">
                                        Managed
                                      </AppBadge>
                                    </span>
                                  </Select.ItemText>
                                  <Select.ItemIndicator className={styles.selectItemIndicator}>
                                    <Check size={14} />
                                  </Select.ItemIndicator>
                                </Select.Item>
                              ))}
                            </Select.Group>
                          </Select.Viewport>
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                    {errors.adAccountId && (
                      <span className={styles.error} role="alert">
                        {errors.adAccountId.message}
                      </span>
                    )}
                  </div>
                )}
              />
            ) : (
              <Controller
                name="adAccountId"
                control={control}
                render={({ field }) => (
                  <AppSelect
                    label="Ad Account"
                    placeholder="Select an ad account..."
                    options={accountOptions.map((opt) => {
                      const acc = activeAccounts.find((a) => a.id === opt.value);
                      return {
                        ...opt,
                        label: acc?.isManaged ? `${opt.label} (Managed)` : opt.label,
                      };
                    })}
                    value={field.value}
                    onValueChange={field.onChange}
                    error={errors.adAccountId?.message}
                  />
                )}
              />
            )}
          </AppCard>
        </div>
      )}

      {selectedPlatform === 'META' && isSelfManaged && watchedAdAccountId && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Facebook Page</h2>
          <AppCard className={styles.fieldCard}>
            <Controller
              name="pageId"
              control={control}
              render={({ field }) => (
                <MetaPagePicker
                  adAccountId={watchedAdAccountId}
                  value={field.value || undefined}
                  selectedName={watch('pageName') || undefined}
                  onChange={(id, name) => {
                    field.onChange(id);
                    setValue('pageName', name, { shouldDirty: true });
                  }}
                  error={errors.pageId?.message}
                />
              )}
            />
          </AppCard>
        </div>
      )}

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Campaign Information</h2>
        <AppInput
          label="Campaign Name"
          placeholder="e.g., Summer Music Festival 2025 - Ticket Push"
          error={errors.name?.message}
          {...register('name')}
        />
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Campaign Objective</h2>
        <p className={styles.sectionDescription}>
          What do you want to achieve with this campaign?
          {selectedPlatform && filteredObjectiveOptions.length < OBJECTIVE_OPTIONS.length && (
            <span className={styles.objectiveFilterNote}>
              {' '}
              Showing objectives available for {selectedPlatform}.
            </span>
          )}
        </p>

        <Controller
          name="objective"
          control={control}
          render={({ field }) => (
            <div className={styles.objectiveGrid}>
              {filteredObjectiveOptions.map((opt) => {
                const Icon = OBJECTIVE_ICONS[opt.icon] ?? Target;
                const isSelected = field.value === opt.value;

                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={clsx(styles.objectiveCard, isSelected && styles.objectiveSelected)}
                    onClick={() => field.onChange(opt.value)}
                    aria-pressed={isSelected}
                  >
                    <div className={styles.objectiveIcon}>
                      <Icon size={24} />
                    </div>
                    <span className={styles.objectiveLabel}>{opt.label}</span>
                    <span className={styles.objectiveDescription}>{opt.description}</span>
                  </button>
                );
              })}
              {errors.objective && (
                <span className={styles.error} role="alert">
                  {errors.objective.message}
                </span>
              )}
            </div>
          )}
        />

        {objectiveNote && (
          <div className={styles.objectiveNote}>
            <Info size={14} />
            <span>{objectiveNote}</span>
          </div>
        )}
      </div>

      {requiresPixel(selectedObjective, selectedPlatform as AdPlatform) && (
        <div className={styles.section}>
          <ConversionPixelFields form={form} adAccountId={watch('adAccountId')} />
        </div>
      )}

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Budget</h2>
        <p className={styles.sectionDescription}>
          Pick how you want to pace spend. Minimum daily budget for {platform}:{' '}
          {formatMinBudget(platform)}.
        </p>

        <div className={styles.budgetStrategyToggle} role="radiogroup" aria-label="Budget type">
          <button
            type="button"
            role="radio"
            aria-checked={budgetStrategy === 'daily'}
            className={clsx(
              styles.budgetStrategyOption,
              budgetStrategy === 'daily' && styles.budgetStrategyOptionActive,
            )}
            onClick={() => handleStrategyChange('daily')}
          >
            <strong>Daily budget</strong>
            <span>Spend up to a fixed amount each day.</span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={budgetStrategy === 'lifetime'}
            className={clsx(
              styles.budgetStrategyOption,
              budgetStrategy === 'lifetime' && styles.budgetStrategyOptionActive,
            )}
            onClick={() => handleStrategyChange('lifetime')}
          >
            <strong>Lifetime budget</strong>
            <span>Set a total and let the platform pace it across the schedule.</span>
          </button>
        </div>

        <div className={styles.budgetRow}>
          {/* Both fields stay mounted (hidden via CSS rather than removed from
              the tree) so React Hook Form never unregisters the inactive
              field's value on strategy switch -- an unmounted Controller's
              field becomes `undefined`, which fails the schema's base
              `z.number()` check for that field regardless of the strategy
              actually selected. */}
          <div className={clsx(budgetStrategy !== 'daily' && styles.budgetFieldHidden)}>
            <Controller
              name="dailyBudgetCents"
              control={control}
              render={({ field }) => (
                <BudgetInput
                  label="Daily Budget"
                  currency="USD"
                  value={Number.isFinite(field.value) ? (field.value / 100).toFixed(2) : ''}
                  onChange={(cents) => field.onChange(cents)}
                  error={
                    errors.dailyBudgetCents?.message ??
                    (field.value > 0 && field.value < minDailyBudget
                      ? `Minimum daily budget is ${formatMinBudget(platform)}`
                      : undefined)
                  }
                  className={styles.budgetField}
                />
              )}
            />
          </div>
          <div className={clsx(budgetStrategy !== 'lifetime' && styles.budgetFieldHidden)}>
            <Controller
              name="totalBudgetCents"
              control={control}
              render={({ field }) => (
                <BudgetInput
                  label="Lifetime Budget"
                  currency="USD"
                  value={Number.isFinite(field.value) ? (field.value / 100).toFixed(2) : ''}
                  onChange={(cents) => field.onChange(cents)}
                  error={errors.totalBudgetCents?.message}
                  helperText="Total the platform will pace across the campaign."
                  className={styles.budgetField}
                />
              )}
            />
          </div>
        </div>

        {budgetRecommendationEnabled &&
          budgetSuggestionMutation.data &&
          budgetStrategy === 'daily' && (
            <BudgetPacingBreakdown
              dailyBudgetCents={
                (budgetSuggestionMutation.data.payload['dailyBudgetCents'] as number) ?? 0
              }
              totalBudgetCents={
                (budgetSuggestionMutation.data.payload['totalBudgetCents'] as number) ?? 0
              }
              estimatedReach={
                budgetSuggestionMutation.data.payload['estimatedReach'] as number | undefined
              }
              estimatedClicks={
                budgetSuggestionMutation.data.payload['estimatedClicks'] as number | undefined
              }
              estimatedConversions={
                budgetSuggestionMutation.data.payload['estimatedConversions'] as number | undefined
              }
              estimatedCostPerConversionCents={
                budgetSuggestionMutation.data.payload['estimatedCostPerConversionCents'] as
                  | number
                  | undefined
              }
              confidence={budgetSuggestionMutation.data.payload['confidence'] as string | undefined}
              pacing={
                budgetSuggestionMutation.data.payload['pacing'] as
                  | {
                      currentPhase: string;
                      budgetMultiplier: number;
                      explanation: string;
                      daysUntilEvent: number;
                    }
                  | undefined
              }
              onApply={handleApplyBudgetSuggestion}
            />
          )}
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Schedule</h2>
        <div className={styles.dateRow}>
          <AppInput
            label="Start Date"
            type="date"
            min={todayInputValue()}
            error={errors.startDate?.message}
            {...register('startDate')}
          />
          <AppInput
            label="End Date"
            type="date"
            min={startDate || todayInputValue()}
            error={errors.endDate?.message}
            {...register('endDate')}
          />
        </div>
      </div>

      <div className={styles.actions}>
        <AppButton type="button" variant="ghost" onClick={onSaveDraft} loading={isSaving}>
          Save as Draft
        </AppButton>
        <div className={styles.navActions}>
          {showBack && (
            <AppButton type="button" variant="secondary" onClick={onBack}>
              Back
            </AppButton>
          )}
          <AppButton type="submit">Continue</AppButton>
        </div>
      </div>
    </form>
  );
}
